<template>
  <div class="login-page">
    <div class="login-page__shade"></div>

    <aside class="login-benefits" aria-label="Avantages Suite241 Gestion Commerciale">
      <article class="login-benefit">
        <span class="login-benefit__icon">
          <i class="fa fa-box-open"></i>
        </span>
        <div>
          <h2>Stock en temps réel</h2>
          <p>Suivez chaque mouvement de vos produits.</p>
        </div>
      </article>

      <article class="login-benefit">
        <span class="login-benefit__icon">
          <i class="fa fa-chart-simple"></i>
        </span>
        <div>
          <h2>Rapports et analyses</h2>
          <p>Prenez les bonnes décisions grâce à des rapports clairs.</p>
        </div>
      </article>

      <article class="login-benefit">
        <span class="login-benefit__icon">
          <i class="fa fa-shield-halved"></i>
        </span>
        <div>
          <h2>Sécurisé et fiable</h2>
          <p>Vos données sont protégées et sauvegardées.</p>
        </div>
      </article>
    </aside>

    <main class="login-main">
      <section class="login-card" aria-labelledby="loginTitle">
        <header class="login-card__header">
          <img class="login-logo" src="../assets/logo.png" alt="Suite241 Gestion Commerciale" />
        </header>

        <div class="login-card__welcome">
          <h1 id="loginTitle">Bienvenue !</h1>
          <p>Connectez-vous pour accéder à votre espace</p>
        </div>

        <div v-if="errorMessage" class="login-alert" role="alert">
          {{ errorMessage }}
        </div>

        <form class="login-form" @submit.prevent="handleLogin">
          <div class="login-field">
            <label for="username">Nom d'utilisateur ou email</label>
            <span class="login-input">
              <i class="fa fa-user"></i>
              <PrimevueInputText
                id="username"
                v-model="form.username"
                type="text"
                autocomplete="username"
                placeholder="Entrez votre email ou nom d'utilisateur"
                required
              />
            </span>
          </div>

          <div class="login-field">
            <label for="password">Mot de passe</label>
            <span class="login-input">
              <i class="fa fa-lock"></i>
              <PrimevueInputText
                id="password"
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                placeholder="Entrez votre mot de passe"
                required
              />
              <button
                type="button"
                class="login-password-toggle"
                :aria-label="showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
                @click="showPassword = !showPassword"
              >
                <i class="fa" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
              </button>
            </span>
          </div>

          <div class="login-options">
            <label class="login-remember" for="rememberMe">
              <PrimevueCheckbox
                id="rememberMe"
                v-model="rememberMe"
                :binary="true"
              />
              <span>Se souvenir de moi</span>
            </label>
            <button type="button" class="login-forgot-link" @click="openForgotPasswordDialog">
              <i class="fa fa-key me-1" aria-hidden="true"></i>
              Mot de passe oublié ?
            </button>
          </div>

          <button
            type="submit"
            class="login-submit"
            :disabled="isLoading"
          >
            <i class="fa" :class="isLoading ? 'fa-spinner fa-spin' : 'fa-right-to-bracket'"></i>
            <span v-if="isLoading">Connexion...</span>
            <span v-else>Se connecter</span>
          </button>

          <!--
          <div class="login-separator">
            <span></span>
            <strong>ou</strong>
            <span></span>
          </div>

          <button type="button" class="login-google">
            <i class="fa-brands fa-google"></i>
            <span>Continuer avec Google</span>
          </button>
          -->
        </form>

        <!--
        <footer class="login-card__footer">
          <p>Vous n'avez pas de compte ?</p>
          <a href="#" @click.prevent>Contactez l’administrateur</a>
        </footer>
        -->
      </section>
    </main>

    <p class="login-copyright">© 2024 Suite241 Gestion Commerciale. Tous droits réservés.</p>

    <PrimevueDialog
      v-model:visible="forgotPasswordDialogVisible"
      header="Mot de passe oublié"
      modal
      :draggable="false"
      :style="{ width: 'min(92vw, 430px)' }"
      class="forgot-password-dialog"
    >
      <form class="forgot-password-form" @submit.prevent="submitForgotPassword">
        <p>
          Entrez l'adresse email enregistrée sur votre compte. Si elle existe et que le compte est actif,
          vous recevrez un lien pour choisir un nouveau mot de passe.
        </p>

        <div class="login-field">
          <label for="forgotPasswordEmail">Adresse email du compte</label>
          <span class="login-input">
            <i class="fa fa-envelope"></i>
            <PrimevueInputText
              id="forgotPasswordEmail"
              v-model="forgotPasswordEmail"
              type="email"
              autocomplete="email"
              placeholder="exemple@domaine.com"
              required
            />
          </span>
        </div>

        <div v-if="forgotPasswordError" class="forgot-password-feedback forgot-password-feedback--error">
          {{ forgotPasswordError }}
        </div>

        <div v-if="forgotPasswordMessage" class="forgot-password-feedback forgot-password-feedback--success">
          {{ forgotPasswordMessage }}
        </div>

        <div class="forgot-password-actions">
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="isRequestingPasswordReset"
            @click="forgotPasswordDialogVisible = false"
          >
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Annuler
          </button>
          <button
            type="submit"
            class="login-submit forgot-password-submit"
            :disabled="isRequestingPasswordReset"
          >
            <i class="fa" :class="isRequestingPasswordReset ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
            <span v-if="isRequestingPasswordReset">Envoi...</span>
            <span v-else>Envoyer</span>
          </button>
        </div>
      </form>
    </PrimevueDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { extractApiErrorMessage } from '@/support/apiErrors';

export default defineComponent({
  name: 'LoginView',

  setup() {
    const store = useStore();
    const router = useRouter();

    const form = reactive({
      username: '',
      password: '',
    });
    const isLoading = ref(false);
    const errorMessage = ref('');
    const rememberMe = ref(true);
    const showPassword = ref(false);
    const forgotPasswordDialogVisible = ref(false);
    const forgotPasswordEmail = ref('');
    const forgotPasswordMessage = ref('');
    const forgotPasswordError = ref('');
    const isRequestingPasswordReset = ref(false);

    const handleLogin = async () => {
      errorMessage.value = '';
      isLoading.value = true;

      try {
        const data = await store.dispatch('auth/login', {
          username: form.username,
          password: form.password,
        });

        if (data.must_change_password) {
          router.push({ name: 'ChangePassword' });
        } else if (data.user?.role === 'caissier') {
          router.push({ name: 'CashierSales' });
        } else if (
          data.user?.role === 'utilisateur'
          && !data.user?.shop_id
          && Array.isArray(data.user?.accessible_shops)
          && data.user.accessible_shops.length > 1
        ) {
          router.push({ name: 'Sites' });
        } else {
          router.push({ name: 'Dashboard' });
        }
      } catch (error: unknown) {
        errorMessage.value = extractApiErrorMessage(error, 'Une erreur est survenue lors de la connexion.');
      } finally {
        isLoading.value = false;
      }
    };

    const openForgotPasswordDialog = () => {
      forgotPasswordEmail.value = form.username.includes('@') ? form.username : '';
      forgotPasswordMessage.value = '';
      forgotPasswordError.value = '';
      forgotPasswordDialogVisible.value = true;
    };

    const submitForgotPassword = async () => {
      forgotPasswordMessage.value = '';
      forgotPasswordError.value = '';
      isRequestingPasswordReset.value = true;

      try {
        const response = await store.dispatch('auth/forgotPassword', {
          email: forgotPasswordEmail.value,
        });

        forgotPasswordMessage.value = response.message || 'Si ce compte peut être récupéré, un lien de réinitialisation vous sera envoyé par email.';
      } catch (error: unknown) {
        forgotPasswordError.value = extractApiErrorMessage(error, "Impossible d'envoyer la réinitialisation.");
      } finally {
        isRequestingPasswordReset.value = false;
      }
    };

    return {
      form,
      isLoading,
      errorMessage,
      rememberMe,
      showPassword,
      forgotPasswordDialogVisible,
      forgotPasswordEmail,
      forgotPasswordMessage,
      forgotPasswordError,
      isRequestingPasswordReset,
      handleLogin,
      openForgotPasswordDialog,
      submitForgotPassword,
    };
  },
});
</script>

<style scoped>
.login-page {
  --login-page-padding-x: clamp(0.9rem, 1.5vw, 1.5rem);
  --login-page-padding-y: clamp(1.1rem, 2vh, 2.5rem);
  --login-page-padding-bottom: clamp(3rem, 4vh, 4.5rem);
  --login-card-padding: clamp(1rem, 1.2vw + 0.65rem, 1.75rem);
  --login-card-radius: clamp(0.95rem, 0.75rem + 0.42vw, 1.25rem);
  --login-card-width: 33.75rem;
  --login-logo-size: clamp(4.25rem, 3.45rem + 1.6vw, 5.25rem);
  --login-logo-radius: clamp(0.9rem, 0.72rem + 0.38vw, 1.2rem);
  --login-title-size: clamp(1.28rem, 1.1rem + 0.48vw, 1.58rem);
  --login-field-height: clamp(2.95rem, 2.65rem + 0.48vw, 3.25rem);
  --login-button-height: clamp(3rem, 2.7rem + 0.55vw, 3.35rem);
  position: relative;
  display: grid;
  min-height: 100svh;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  background-image: url('../assets/bg-login.png');
  background-position: center;
  background-size: cover;
  color: #101c36;
}

.login-page::before {
  position: absolute;
  inset: 0;
  z-index: 0;
  content: '';
  backdrop-filter: blur(5px);
  background:
    radial-gradient(circle at 50% 88%, rgba(255, 255, 255, 0.28), transparent 16rem),
    rgba(1, 14, 20, 0.38);
}

.login-page__shade {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background:
    linear-gradient(90deg, rgba(2, 37, 38, 0.86) 0%, rgba(2, 43, 42, 0.58) 24%, rgba(3, 18, 26, 0.08) 57%, rgba(3, 14, 20, 0.28) 100%),
    linear-gradient(180deg, rgba(3, 10, 17, 0.26), rgba(3, 10, 17, 0.24));
}

.login-main {
  position: relative;
  z-index: 2;
  display: flex;
  min-height: 100svh;
  align-items: center;
  justify-content: center;
  padding: var(--login-page-padding-y) var(--login-page-padding-x) var(--login-page-padding-bottom);
}

.login-card {
  width: min(100%, var(--login-card-width));
  max-width: calc(100vw - (var(--login-page-padding-x) * 2));
  padding: var(--login-card-padding);
  border: 1px solid rgba(255, 255, 255, 0.78);
  border-radius: var(--login-card-radius);
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 1.4rem 4rem rgba(1, 12, 20, 0.34);
}

.login-card__header {
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-bottom: clamp(0.9rem, 1.1vw, 1.35rem);
  text-align: center;
}

.login-logo {
  width: var(--login-logo-size);
  height: var(--login-logo-size);
  border-radius: var(--login-logo-radius);
  object-fit: contain;
}

.login-card__welcome p,
.login-card__footer p {
  margin: 0;
  color: #6e7893;
}

.login-card__welcome {
  margin-bottom: clamp(1rem, 1vw, 1.35rem);
  text-align: center;
}

.login-card__welcome h1 {
  margin: 0 0 0.45rem;
  font-size: var(--login-title-size);
  font-weight: 800;
  color: #101c36;
}

.login-alert {
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(220, 53, 69, 0.25);
  border-radius: 12px;
  background: rgba(220, 53, 69, 0.1);
  color: #b02a37;
  font-weight: 700;
  text-align: center;
}


.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.15rem;
}



.login-field {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.login-field label {
  margin: 0;
  color: #303c58;
  font-size: 0.94rem;
  font-weight: 800;
}

.login-input {
  position: relative;
  display: flex;
  align-items: center;
}

.login-input > i {
  position: absolute;
  left: 1.1rem;
  z-index: 2;
  color: #697897;
  font-size: 1.04rem;
}

.login-input :deep(.p-inputtext) {
  width: 100%;
  min-height: var(--login-field-height);
  padding: 0.75rem 3.2rem 0.75rem 3.25rem;
  border: 1px solid #d8deea;
  border-radius: 0.6rem;
  background: rgba(255, 255, 255, 0.92);
  color: #101c36;
  font-weight: 600;
  box-shadow: none;
}

.login-input :deep(.p-inputtext::placeholder) {
  color: #8b95ad;
  font-weight: 500;
}

.login-input :deep(.p-inputtext:enabled:focus) {
  border-color: #079e75;
  box-shadow: 0 0 0 0.2rem rgba(7, 158, 117, 0.14);
}

.login-password-toggle {
  position: absolute;
  right: 1rem;
  z-index: 3;
  display: inline-flex;
  width: 2.25rem;
  height: 2.25rem;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #697897;
}

.login-password-toggle:hover,
.login-password-toggle:focus-visible {
  color: #079e75;
}

.login-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  color: #4d5975;
  font-size: 0.95rem;
  font-weight: 700;
}

.login-remember {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.login-remember :deep(.p-checkbox) {
  width: 1.1rem;
  height: 1.1rem;
}

.login-remember :deep(.p-checkbox-box) {
  width: 1.1rem;
  height: 1.1rem;
  border-color: #079e75;
  border-radius: 3px;
}

.login-remember :deep(.p-checkbox-box.p-highlight) {
  border-color: #079e75;
  background: #079e75;
}

.login-forgot-link,
.login-card__footer a {
  border: 0;
  background: transparent;
  color: #079e75;
  font-weight: 800;
  padding: 0;
  text-decoration: none;
}

.login-forgot-link:hover,
.login-forgot-link:focus-visible,
.login-card__footer a:hover {
  color: #057b5d;
  text-decoration: underline;
}

.login-submit {
  display: inline-flex;
  min-height: var(--login-button-height);
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 0.25rem;
  border: 0;
  border-radius: 0.6rem;
  background: linear-gradient(135deg, #12b68a 0%, #047c62 100%);
  color: #fff;
  font-size: 1.03rem;
  font-weight: 800;
  box-shadow: 0 0.9rem 1.8rem rgba(5, 132, 101, 0.22);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.login-submit:hover:not(:disabled),
.login-submit:focus-visible:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 1.1rem 2rem rgba(5, 132, 101, 0.3);
}

.login-submit:disabled {
  cursor: not-allowed;
  opacity: 0.75;
}



.login-separator {
  display: grid;
  align-items: center;
  grid-template-columns: 1fr auto 1fr;
  gap: 1rem;
  color: #6e7893;
}

.login-separator span {
  height: 1px;
  background: #dfe4ee;
}

.login-separator strong {
  font-size: 0.95rem;
}

.login-google {
  display: inline-flex;
  min-height: 3rem;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  border: 1px solid #d8deea;
  border-radius: 0.6rem;
  background: #fff;
  color: #26334f;
  font-weight: 800;
}

.login-card__footer {
  margin-top: 2rem;
  text-align: center;
}

.forgot-password-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.forgot-password-form p {
  margin: 0;
  color: #5c6883;
  line-height: 1.5;
}

.forgot-password-feedback {
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  font-weight: 700;
}

.forgot-password-feedback--error {
  border: 1px solid rgba(220, 53, 69, 0.18);
  background: rgba(220, 53, 69, 0.08);
  color: #b02a37;
}

.forgot-password-feedback--success {
  border: 1px solid rgba(7, 158, 117, 0.18);
  background: rgba(7, 158, 117, 0.08);
  color: #057b5d;
}

.forgot-password-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.forgot-password-submit {
  width: auto;
  min-width: 8.75rem;
  min-height: 2.75rem;
  padding: 0 1.2rem;
}

.login-benefits {
  position: absolute;
  top: 50%;
  left: clamp(1.5rem, 4vw, 3.75rem);
  z-index: 2;
  display: flex;
  width: min(21rem, 26vw);
  flex-direction: column;
  gap: clamp(2rem, 5vw, 4.1rem);
  transform: translateY(-43%);
  color: #fff;
}

.login-benefit {
  display: grid;
  align-items: start;
  grid-template-columns: clamp(3.5rem, 3.2vw, 4rem) 1fr;
  gap: 1rem;
}

.login-benefit__icon {
  display: inline-flex;
  width: clamp(3.5rem, 3.2vw, 4rem);
  height: clamp(3.5rem, 3.2vw, 4rem);
  align-items: center;
  justify-content: center;
  border-radius: clamp(0.9rem, 0.8vw, 1.125rem);
  background: linear-gradient(135deg, rgba(20, 187, 143, 0.96), rgba(4, 105, 91, 0.9));
  box-shadow: 0 1rem 2.4rem rgba(3, 21, 22, 0.38);
  font-size: 1.7rem;
}

.login-benefit h2 {
  margin: 0 0 0.45rem;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 800;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.28);
}

.login-benefit p {
  margin: 0;
  color: rgba(255, 255, 255, 0.92);
  font-size: 1rem;
  line-height: 1.45;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.28);
}

.login-copyright {
  position: absolute;
  bottom: 1.55rem;
  left: 50%;
  z-index: 2;
  margin: 0;
  color: rgba(255, 255, 255, 0.92);
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
  transform: translateX(-50%);
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.32);
}

@media (max-width: 1180px) {
  .login-benefits {
    display: none;
  }
}

@media (max-width: 640px) {
  .login-main {
    align-items: flex-start;
    padding: 0.85rem 0.85rem 3.25rem;
  }

  .login-page {
    --login-card-padding: clamp(0.85rem, 3.6vw, 1.2rem);
    --login-card-radius: 0.9rem;
    --login-logo-size: clamp(3.5rem, 14vw, 4rem);
    --login-logo-radius: 0.95rem;
    --login-title-size: clamp(1.16rem, 4.2vw, 1.32rem);
    --login-field-height: 3rem;
    --login-button-height: 3.05rem;
  }

  .login-card {
    width: 100%;
    max-width: 28rem;
    margin: auto 0;
  }

  .login-card__header {
    margin-bottom: 1rem;
  }

  .login-logo {
    width: var(--login-logo-size);
    height: var(--login-logo-size);
    border-radius: var(--login-logo-radius);
  }

  .login-card__welcome {
    margin-bottom: 1.2rem;
  }

  .login-card__welcome h1 {
    font-size: var(--login-title-size);
  }

  .login-card__welcome p {
    font-size: 0.9rem;
  }

  .login-form {
    gap: 0.9rem;
  }


  .login-field {
    gap: 0.42rem;
  }

  .login-field label,
  .login-options {
    font-size: 0.86rem;
  }

  .login-input :deep(.p-inputtext) {
    min-height: var(--login-field-height);
    padding-left: 2.85rem;
    padding-right: 2.85rem;
  }

  .login-input > i {
    left: 0.95rem;
    font-size: 0.96rem;
  }

  .login-password-toggle {
    right: 0.65rem;
  }

  .login-submit {
    min-height: var(--login-button-height);
    font-size: 0.95rem;
  }

  .login-options {
    align-items: flex-start;
    flex-direction: column;
  }

  .login-copyright {
    width: calc(100% - 2rem);
    font-size: 0.88rem;
  }
}

@media (max-width: 380px), (max-height: 680px) {
  .login-page {
    --login-card-padding: 0.85rem;
    --login-logo-size: 3.25rem;
    --login-title-size: 1.16rem;
    --login-field-height: 2.85rem;
    --login-button-height: 2.95rem;
  }

  .login-main {
    padding-top: 0.65rem;
  }

  .login-card__header {
    margin-bottom: 0.8rem;
  }

  .login-card__welcome {
    margin-bottom: 1rem;
  }
}
</style>
